import { writeWorkbook, downloadBlob } from './xlsx';

// The Excel interchange format for Payment Porter rules — one place that defines the sheets, builds the
// example workbook, and parses an uploaded one back into the payload POSTed to /paymentporter/{id}/rules-import.
//
// Shape follows the database rather than the screens: a rule row per rule, and a child row per criteria
// item, joined on the rule NAME (rules have no id until they are imported, so the name is the only key the
// author can write). Names are a within-file join key only — the server resolves them to real ids.
//
// Routing and profile assignment live as COLUMNS on the Rules sheet, not their own sheets, because
// rule-helper.resolveRuleConfig reads one active routing row and one active profile assignment per rule
// (both `LIMIT 1`). Modelling them as sheets would invite authors to enter several and silently lose all
// but one.
//
// Everything imported is created INACTIVE — see the note on buildImportPayload.

export const AGENT_TYPES = ['OTA', 'COM', 'SRC', 'TRA'];
export const IATA_TYPES  = ['AGT', 'SRC', 'COM'];
export const AUTH_CODES   = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
export const WINDOWS      = ['1', '2', '3', '4', '5', '6', '7', '8'];
export const ROUTING_TYPES = ['Window Routing', 'Room Routing'];

export const SHEETS = {
    rules:      'Rules',
    rateCodes:  'Rate Codes',
    payment:    'Payment Methods',
    corporate:  'Corporate Profiles',
    iata:       'IATA Profiles',
};

// header → the key the parser produces. Headers are matched case-insensitively with any trailing "*"
// stripped, so columns may be reordered and the required-marker may be dropped without breaking the import.
const COLUMNS = {
    [SHEETS.rules]: [
        { header: 'Rule Name *',            key: 'name',                width: 28 },
        { header: 'Description',            key: 'description',         width: 34 },
        { header: 'Sequence',               key: 'sequence',            width: 10 },
        { header: 'Window',                 key: 'window_number',       width: 9 },
        { header: 'Routing Type',           key: 'routing_type',        width: 16 },
        { header: 'Routing Code',           key: 'routing_code',        width: 14 },
        { header: 'Routing Description',    key: 'routing_description', width: 34 },
        { header: 'Routing Window',         key: 'routing_window',      width: 15 },
        { header: 'Authorization Rule Code', key: 'auth_code',          width: 22 },
        { header: 'Authorization Amount',   key: 'auth_amount',         width: 20 },
        { header: 'Assign Profile ID',      key: 'assign_profile_id',   width: 18 },
        { header: 'Assign Profile Name',    key: 'assign_profile_name', width: 26 },
        { header: 'Assign Profile Type',    key: 'assign_profile_type', width: 18 },
        { header: 'Assign Corporate ID',    key: 'assign_corporate_id', width: 19 },
    ],
    [SHEETS.rateCodes]: [
        { header: 'Rule Name *',     key: 'rule',           width: 28 },
        { header: 'Rate Code *',     key: 'rate_code',      width: 16 },
        { header: 'Rate Code Name *', key: 'rate_code_name', width: 34 },
    ],
    [SHEETS.payment]: [
        { header: 'Rule Name *',           key: 'rule',        width: 28 },
        { header: 'Payment Method Code *', key: 'code',        width: 22 },
        { header: 'Description',           key: 'description', width: 30 },
    ],
    [SHEETS.corporate]: [
        { header: 'Rule Name *',  key: 'rule',       width: 28 },
        { header: 'Agent Type *', key: 'agent_type', width: 13 },
        { header: 'Profile ID *', key: 'profile_id', width: 15 },
        { header: 'Name *',       key: 'name',       width: 30 },
        { header: 'Address',      key: 'address',    width: 28 },
        { header: 'City',         key: 'city',       width: 18 },
        { header: 'Country',      key: 'country',    width: 10 },
    ],
    [SHEETS.iata]: [
        { header: 'Rule Name *',        key: 'rule',         width: 28 },
        { header: 'Profile Type *',     key: 'profile_type', width: 14 },
        { header: 'IATA / Corporate ID *', key: 'corporate_id', width: 21 },
        { header: 'Name *',             key: 'name',         width: 30 },
        { header: 'Address',            key: 'address',      width: 28 },
        { header: 'City',               key: 'city',         width: 18 },
        { header: 'Country',            key: 'country',      width: 10 },
    ],
};

const normHeader = (h) => String(h || '').replace(/\*/g, '').trim().toLowerCase();
const normSheet  = (n) => String(n || '').trim().toLowerCase();

// ─── Example workbook ────────────────────────────────────────────────────────

const README_ROWS = [
    ['Payment Porter — bulk rule upload'],
    [],
    ['How to use'],
    ['1.', 'Fill in one row per rule on the "Rules" sheet. The Rule Name is what links every other sheet to it.'],
    ['2.', 'Add the rule\'s selection criteria on "Rate Codes", "Corporate Profiles" or "IATA Profiles" — one of those three per rule.'],
    ['3.', 'Optionally narrow the rule further with card types on "Payment Methods".'],
    ['4.', 'Upload the file with "Upload Rules" on the porter page, check the preview, then confirm.'],
    [],
    ['Rules you must follow'],
    ['', 'Rule Name is required, must be unique in this file, and must NOT already exist on the porter —'],
    ['', 'a name that already exists REJECTS the whole file. Nothing is imported until you rename it.'],
    ['', 'Each rule needs criteria on EXACTLY ONE of Rate Codes / Corporate Profiles / IATA Profiles — the rule screen shows one criteria type per rule.'],
    ['', 'Payment Methods are an AND filter: a reservation must match a payment method AND one of the other criteria.'],
    ['', 'A RATE CODE already used by any rule on this porter also REJECTS the whole file. Two rules matching'],
    ['', 'the same rate code makes it depend on sequence which one handles a reservation. Remove it from the'],
    ['', 'file, or from the existing rule first. (Payment methods and profile ids may repeat across rules.)'],
    ['', 'A rule with only payment methods and no other criteria is rejected — it would match every reservation paid by that card type.'],
    ['', 'Delete the example rows before uploading. Blank rows are ignored.'],
    [],
    ['Allowed values'],
    ['Window', WINDOWS.join(', ')],
    ['Routing Type', ROUTING_TYPES.join(', ')],
    ['Authorization Rule Code', AUTH_CODES.join(', ') + '  (leave blank for none; an Amount without a Code is rejected)'],
    ['Agent Type', 'OTA = Online Travel Agent, COM = Company, SRC = Source, TRA = Travel Agent'],
    ['Profile Type (IATA)', 'AGT = Agent, SRC = Source, COM = Company'],
    [],
    ['After the upload'],
    ['', 'Every imported rule is created INACTIVE. Nothing runs against the PMS until you switch it on.'],
    ['', 'Open each rule, use Test → Details → Dry Run to confirm it does what you expect, then activate it.'],
];

// Modelled on two real OTA rules so the relationship between the sheets is obvious at a glance.
const EXAMPLE = {
    [SHEETS.rules]: [
        ['Expedia',     'Route Expedia bookings to window 2', 1, '', 'Window Routing', 'RTHF', 'Route to house account', '2', '1', '', '', '', '', ''],
        ['Booking.com', 'Route Booking.com bookings to window 2', 2, '', 'Window Routing', 'RTHF', 'Route to house account', '2', '1', '', '', '', '', ''],
        ['Amex Corporate', 'Corporate travel billed back to the company', 3, '', 'Window Routing', 'RTCO', 'Route to company account', '3', '', '', '', '', '', ''],
    ],
    [SHEETS.rateCodes]: [
        ['Expedia',     'EXP3RD', 'Expedia 3rd Party'],
        ['Expedia',     'WOEXP',  'Wholesale Expedia'],
        ['Booking.com', 'WOBKG',  'Wholesale Booking.com'],
    ],
    [SHEETS.payment]: [
        ['Expedia',     'MC', 'Mastercard'],
        ['Expedia',     'VA', 'Visa'],
        ['Booking.com', 'MC', 'Mastercard'],
        ['Booking.com', 'VA', 'Visa'],
    ],
    [SHEETS.corporate]: [
        ['Amex Corporate', 'COM', '123456', 'American Express Travel', '', 'London', 'GB'],
    ],
    [SHEETS.iata]: [
        ['', '', '', '', '', '', ''],
    ],
};

/** Build the example/template workbook and start the download. */
export const downloadRuleTemplate = async (porterName) => {
    const blob = await writeWorkbook([
        // The Read Me sheet's title IS its header row, so its body starts at README_ROWS[1].
        {
            name: 'Read Me',
            columns: [{ header: README_ROWS[0][0], width: 30 }, { header: '', width: 110 }],
            rows: README_ROWS.slice(1),
        },
        ...Object.values(SHEETS).map(name => ({
            name,
            columns: COLUMNS[name],
            rows: EXAMPLE[name] || [],
        })),
    ]);
    const slug = String(porterName || 'payment-porter').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadBlob(blob, `${slug}-rules-template.xlsx`);
};

// ─── Parse ───────────────────────────────────────────────────────────────────

// Map a sheet's rows onto {key: value} objects using its header row, so column order does not matter.
const objectsFrom = (rows, sheetName) => {
    if (!rows || !rows.length) return [];
    const defs = COLUMNS[sheetName];
    const header = rows[0].map(normHeader);
    const idx = {};
    defs.forEach(d => { idx[d.key] = header.indexOf(normHeader(d.header)); });
    return rows.slice(1)
        .map((row, i) => {
            const o = { __row: i + 2 }; // 1-based, +1 for the header — this is the row number Excel shows
            defs.forEach(d => { o[d.key] = idx[d.key] >= 0 ? (row[idx[d.key]] || '').trim() : ''; });
            return o;
        })
        .filter(o => defs.some(d => o[d.key]));  // drop rows the author left entirely blank
};

const missingColumns = (rows, sheetName) => {
    if (!rows || !rows.length) return [];
    const header = rows[0].map(normHeader);
    return COLUMNS[sheetName]
        .filter(d => d.header.includes('*') && !header.includes(normHeader(d.header)))
        .map(d => d.header.replace(/\s*\*$/, ''));
};

const num = (v) => (v === '' || v == null ? null : Number(String(v).replace(/,/g, '')));

/**
 * Parse an already-read workbook (see utils/xlsx readWorkbook) into the import payload plus everything the
 * preview needs to show. Never throws on bad content — problems come back as `errors` so the modal can list
 * them against their sheet and row.
 *
 * @param {Object.<string, string[][]>} book        sheet name → rows
 * @param {Array<{name: string}>}       existingRules  the porter's current rules, to flag name clashes
 * @returns {{rules: Array, errors: Array<{where: string, message: string}>, conflicts: string[]}}
 */
export const parseRuleWorkbook = (book, existingRules = []) => {
    const errors = [];
    const err = (where, message) => errors.push({ where, message });

    // Sheets are matched case/whitespace-insensitively; anything else in the file (Read Me, the author's
    // own scratch tabs) is ignored rather than treated as an error.
    const byName = {};
    Object.entries(book).forEach(([name, rows]) => { byName[normSheet(name)] = rows; });
    const sheet = (name) => byName[normSheet(name)] || null;

    const rulesRows = sheet(SHEETS.rules);
    if (!rulesRows || rulesRows.length < 2) {
        err(SHEETS.rules, rulesRows ? 'The sheet has no rule rows — add one row per rule below the headings.' : 'Sheet is missing. Download the example workbook and fill that in.');
        return { rules: [], errors, conflicts: [] };
    }

    // A missing required column silently blanks every value in it, so stop rather than import half a rule.
    for (const name of Object.values(SHEETS)) {
        const rows = sheet(name);
        const missing = missingColumns(rows, name);
        if (missing.length) err(name, `Missing required column(s): ${missing.join(', ')}. Use the example workbook's headings.`);
    }
    if (errors.length) return { rules: [], errors, conflicts: [] };

    const ruleRows = objectsFrom(rulesRows, SHEETS.rules);
    if (!ruleRows.length) {
        err(SHEETS.rules, 'No rules found — every row was blank.');
        return { rules: [], errors, conflicts: [] };
    }

    // ── Rules ────────────────────────────────────────────────────────────────
    const rules = [];
    const byKey = new Map();                       // lower-cased name → rule, for joining the child sheets
    const existing = new Set(existingRules.map(r => String(r.name || '').trim().toLowerCase()));
    const conflicts = [];

    for (const r of ruleRows) {
        const where = `${SHEETS.rules} row ${r.__row}`;
        if (!r.name) { err(where, 'Rule Name is required.'); continue; }
        const key = r.name.toLowerCase();
        if (byKey.has(key)) { err(where, `Duplicate Rule Name "${r.name}" — names must be unique within the file.`); continue; }

        const rule = {
            name: r.name,
            description: r.description || null,
            sequence: null,
            window_number: null,
            rateCodes: [], paymentMethods: [], corporateProfiles: [], iataProfiles: [],
            routing: null, profileAssignment: null,
            __row: r.__row,
            // Marked here, excluded at import time — the preview shows it so the author can rename and retry.
            __conflict: existing.has(key),
        };
        if (rule.__conflict) conflicts.push(r.name);

        if (r.sequence) {
            const n = num(r.sequence);
            if (n == null || !Number.isInteger(n)) err(where, `Sequence "${r.sequence}" must be a whole number.`);
            else rule.sequence = n;
        }
        if (r.window_number) {
            if (!WINDOWS.includes(String(r.window_number))) err(where, `Window "${r.window_number}" must be one of ${WINDOWS.join(', ')}.`);
            else rule.window_number = parseInt(r.window_number, 10);
        }

        // ── Routing action — mirrors AddRoutingPage's validation so an imported rule is exactly what the
        //    screen would have produced. Any routing field filled in commits the author to code + description.
        const anyRouting = r.routing_code || r.routing_description || r.routing_window || r.routing_type || r.auth_code || r.auth_amount;
        if (anyRouting) {
            if (!r.routing_code)        err(where, 'Routing Code is required when any routing column is filled in.');
            if (!r.routing_description) err(where, 'Routing Description is required when any routing column is filled in.');
            if (r.routing_type && !ROUTING_TYPES.includes(r.routing_type)) err(where, `Routing Type "${r.routing_type}" must be one of ${ROUTING_TYPES.join(', ')}.`);
            if (r.routing_window && !WINDOWS.includes(String(r.routing_window))) err(where, `Routing Window "${r.routing_window}" must be one of ${WINDOWS.join(', ')}.`);
            if (r.auth_code && !AUTH_CODES.includes(String(r.auth_code))) err(where, `Authorization Rule Code "${r.auth_code}" must be one of ${AUTH_CODES.join(', ')}.`);
            // An amount with no rule code is stored but never sent to OHIP — the screen disables the field
            // for exactly this reason, so refuse it here rather than import a value that goes nowhere.
            if (r.auth_amount && !r.auth_code) err(where, 'Authorization Amount needs an Authorization Rule Code — an amount on its own is never sent to Opera.');
            if (r.auth_amount) {
                const a = num(r.auth_amount);
                if (a == null || Number.isNaN(a)) err(where, `Authorization Amount "${r.auth_amount}" must be a number.`);
                else if (a < 0)                   err(where, 'Authorization Amount cannot be negative.');
            }
            if (r.routing_code && r.routing_description) {
                rule.routing = {
                    code: r.routing_code,
                    type: r.routing_type || 'Window Routing',
                    description: r.routing_description,
                    default_window: r.routing_window || null,
                    authorization_rule_code: r.auth_code || null,
                    authorization_amount: r.auth_code && r.auth_amount ? String(num(r.auth_amount)) : null,
                };
            }
        }

        if (r.assign_profile_id) {
            rule.profileAssignment = {
                profile_id: r.assign_profile_id,
                profile_name: r.assign_profile_name || null,
                profile_type: r.assign_profile_type || null,
                corporate_id: r.assign_corporate_id || null,
            };
        } else if (r.assign_profile_name || r.assign_profile_type || r.assign_corporate_id) {
            err(where, 'Assign Profile ID is required when any other Assign Profile column is filled in.');
        }

        rules.push(rule);
        byKey.set(key, rule);
    }

    // ── Child sheets ─────────────────────────────────────────────────────────
    // Every child row must name a rule that exists on the Rules sheet; a typo there would otherwise vanish
    // silently and leave a rule with no criteria at all.
    const attach = (sheetName, apply) => {
        for (const row of objectsFrom(sheet(sheetName), sheetName)) {
            const where = `${sheetName} row ${row.__row}`;
            if (!row.rule) { err(where, 'Rule Name is required.'); continue; }
            const rule = byKey.get(row.rule.toLowerCase());
            if (!rule) { err(where, `Rule "${row.rule}" is not on the ${SHEETS.rules} sheet — check the spelling.`); continue; }
            apply(rule, row, where);
        }
    };

    attach(SHEETS.rateCodes, (rule, row, where) => {
        if (!row.rate_code)      return err(where, 'Rate Code is required.');
        if (!row.rate_code_name) return err(where, 'Rate Code Name is required.');
        if (rule.rateCodes.some(x => x.rate_code.toLowerCase() === row.rate_code.toLowerCase())) {
            return err(where, `Rate Code "${row.rate_code}" is already listed for rule "${rule.name}".`);
        }
        rule.rateCodes.push({ rate_code: row.rate_code, rate_code_name: row.rate_code_name });
    });

    attach(SHEETS.payment, (rule, row, where) => {
        if (!row.code) return err(where, 'Payment Method Code is required.');
        if (rule.paymentMethods.some(x => x.code.toLowerCase() === row.code.toLowerCase())) {
            return err(where, `Payment Method "${row.code}" is already listed for rule "${rule.name}".`);
        }
        rule.paymentMethods.push({ code: row.code, description: row.description || null });
    });

    attach(SHEETS.corporate, (rule, row, where) => {
        if (!row.agent_type) return err(where, 'Agent Type is required.');
        if (!AGENT_TYPES.includes(row.agent_type.toUpperCase())) return err(where, `Agent Type "${row.agent_type}" must be one of ${AGENT_TYPES.join(', ')}.`);
        if (!row.profile_id) return err(where, 'Profile ID is required.');
        if (!row.name)       return err(where, 'Name is required.');
        rule.corporateProfiles.push({
            agent_type: row.agent_type.toUpperCase(), profile_id: row.profile_id, name: row.name,
            address: row.address || null, city: row.city || null, country: row.country || null,
        });
    });

    attach(SHEETS.iata, (rule, row, where) => {
        if (!row.profile_type) return err(where, 'Profile Type is required.');
        if (!IATA_TYPES.includes(row.profile_type.toUpperCase())) return err(where, `Profile Type "${row.profile_type}" must be one of ${IATA_TYPES.join(', ')}.`);
        if (!row.corporate_id) return err(where, 'IATA / Corporate ID is required.');
        if (!row.name)         return err(where, 'Name is required.');
        rule.iataProfiles.push({
            profile_type: row.profile_type.toUpperCase(), corporate_id: row.corporate_id, name: row.name,
            address: row.address || null, city: row.city || null, country: row.country || null,
        });
    });

    // ── Per-rule shape ───────────────────────────────────────────────────────
    for (const rule of rules) {
        const where = `${SHEETS.rules} row ${rule.__row}`;
        const types = [
            rule.rateCodes.length        && SHEETS.rateCodes,
            rule.corporateProfiles.length && SHEETS.corporate,
            rule.iataProfiles.length     && SHEETS.iata,
        ].filter(Boolean);

        if (types.length > 1) {
            // The rule screen renders one criteria type per rule (the first non-empty), so the others would
            // be invisible config that still widens what the rule matches.
            err(where, `Rule "${rule.name}" has criteria on ${types.join(' and ')}. A rule may use only one of ${SHEETS.rateCodes}, ${SHEETS.corporate} or ${SHEETS.iata}.`);
        } else if (!types.length) {
            err(where, rule.paymentMethods.length
                ? `Rule "${rule.name}" has payment methods but no ${SHEETS.rateCodes}, ${SHEETS.corporate} or ${SHEETS.iata} criteria — on its own that matches every reservation paid by those card types.`
                : `Rule "${rule.name}" has no criteria. Add rows on ${SHEETS.rateCodes}, ${SHEETS.corporate} or ${SHEETS.iata}.`);
        }
    }

    return { rules, errors, conflicts };
};

/**
 * The POST body for /paymentporter/{id}/rules-import.
 *
 * EVERY parsed rule is sent, including name-clashing ones. This used to drop them, which meant the server
 * never saw a clash and imported the remainder — the client silently deciding what to leave out. A name
 * that already exists now rejects the whole file, and that decision belongs to the server, so the client
 * must hand over what it read rather than a filtered version of it. The modal blocks the button before it
 * gets here; this is the backstop for anything that gets past it.
 *
 * `active` is never sent — the server forces every imported rule inactive. Rules are proven with Dry Run /
 * Run Now on the rule's matched-reservations screen and switched on by hand afterwards.
 */
export const buildImportPayload = (rules) => ({
    rules: rules.map(({ __row, __conflict, ...rule }) => rule),
});
